import { create } from "zustand";
import { ServiceContainer } from "@/core/di";
import type {
  Rule,
  RuleResponse,
  RulesFilters,
} from "@/core/administration/entities/Rule";
import Exception from "@/core/exceptions/Exception";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";

const sc = ServiceContainer.getInstance();
const rulesRepository = sc.getRepository("rules");

export interface RulesState {
  rules: Rule[];
  data: RuleResponse | null;
  loading: boolean;
  filters: RulesFilters;
  failedFetchingRulesException: Exception | null;
  fetchRules: (filters?: RulesFilters) => Promise<void>;
  fetchRuleById: (id: string) => Promise<Rule | null>;
  createRule: (rule: Omit<Rule, "id">) => Promise<Rule | null>;
  updateRule: (id: string, rule: Omit<Rule, "id">) => Promise<Rule | null>;
  deleteRule: (id: string) => Promise<boolean>;
  setSearchFilter: (search: string) => void;
  setStatusFilter: (status: string) => void;
  setPageFilter: (page: number) => void;
  setSizeFilter: (size: number) => void;
  resetFilters: () => void;
}

const defaultFilters: RulesFilters = { search: "", status: "", page: 0, size: 20 };

export const useRulesStore = create<RulesState>((set, get) => ({
  rules: [],
  data: null,
  loading: false,
  filters: defaultFilters,
  failedFetchingRulesException: null,

  fetchRules: async (filters) => {
    const current = get();
    const payload = { ...current.filters, ...filters };
    set({ loading: true, failedFetchingRulesException: null, filters: payload });
    const result = await rulesRepository.getRules(payload);
    if (result.success && result.data) {
      set({
        rules: result.data.data.items,
        data: result.data,
        failedFetchingRulesException: null,
      });
    } else {
      set({
        failedFetchingRulesException: result.exception ?? {
          type: DefaultExceptionTypes.UNKNOWN_ERROR,
          message: "Failed to fetch rules",
        } as Exception,
      });
    }
    set({ loading: false });
  },

  fetchRuleById: async (id) => {
    set({ loading: true });
    const result = await rulesRepository.getRuleById(id);
    set({ loading: false });
    return result.success && result.data ? result.data : null;
  },

  createRule: async (rule) => {
    const result = await rulesRepository.createRule(rule);
    if (result.success && result.data) {
      await get().fetchRules();
      return result.data;
    }
    return null;
  },

  updateRule: async (id, rule) => {
    const result = await rulesRepository.updateRule(id, rule);
    if (result.success && result.data) {
      await get().fetchRules();
      return result.data;
    }
    return null;
  },

  deleteRule: async (id) => {
    const result = await rulesRepository.deleteRule(id);
    if (result.success) {
      await get().fetchRules();
      return true;
    }
    return false;
  },

  setSearchFilter: (search) =>
    set((s) => ({ filters: { ...s.filters, search, page: 0 } })),
  setStatusFilter: (status) =>
    set((s) => ({ filters: { ...s.filters, status, page: 0 } })),
  setPageFilter: (page) => set((s) => ({ filters: { ...s.filters, page } })),
  setSizeFilter: (size) =>
    set((s) => ({ filters: { ...s.filters, size, page: 0 } })),
  resetFilters: () => set({ filters: defaultFilters }),
}));
